import React from 'react';
import {
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
} from '@mui/material';
import {
  VisibilityIcon,
  TuneIcon,
  CheckCircleIcon,
  NotificationsIcon,
  SettingsIcon,
} from '@components/icons/CustomIcons';

interface ScopeDetail {
  name: string;
  displayName: string;
  description: string;
}

interface ScopeListProps {
  /**
   * Array of scope information from backend
   */
  scopes: ScopeDetail[];
}

/**
 * Icon mapping for common device scopes
 */
const scopeIcons: Record<string, JSX.Element> = {
  'devices:read': <VisibilityIcon />,
  'device:read': <VisibilityIcon />,
  'devices:control': <TuneIcon />,
  'device:control': <TuneIcon />,
  'devices:events': <NotificationsIcon />,
  'device:events': <NotificationsIcon />,
  'devices:manage': <SettingsIcon />,
  'device:manage': <SettingsIcon />,
  'offline_access': <CheckCircleIcon />,
};

/**
 * ScopeList component
 * Displays a list of OAuth2 scopes with icons and descriptions.
 * Uses backend-provided displayName and description from scopeDetails.
 */
export const ScopeList: React.FC<ScopeListProps> = ({ scopes }) => {
  /**
   * Get icon for a scope, fallback to CheckCircleIcon
   */
  const getScopeIcon = (scopeName: string): JSX.Element => {
    return scopeIcons[scopeName] || <CheckCircleIcon />;
  };

  if (!scopes || scopes.length === 0) {
    return null;
  }

  return (
    <List dense>
      {scopes.map((scope) => (
        <ListItem key={scope.name} sx={{ py: 1.5 }}>
          <ListItemIcon sx={{ color: 'primary.main' }}>
            {getScopeIcon(scope.name)}
          </ListItemIcon>
          <ListItemText
            primary={scope.displayName}
            secondary={scope.description}
            primaryTypographyProps={{ fontWeight: 500 }}
          />
        </ListItem>
      ))}
    </List>
  );
};
